import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { dispatch } from "../../../../state/store";
import {
  setNotificationCampaignDetails,
  setNotificationCampaignDetailsRefresh,
  setNotificationCampaignToEdit
} from "../../../../state/ducks";

import { campaignStatus } from "../../../../consts";
import { SplittedModalContentTemplate } from "../../../../templates";

import { CustomModalContainer, LineChart, Modal } from "../../../../components";
import { CampaignInfo } from "./components/CampaignInfo/CampaignInfo";
import { Settings } from "./components/Settings/Settings";
import { Loader } from "../../components/Loader/Loader";
import { ReportChart } from "../../components/ReportChart/ReportChart";
import { formatUtils } from "../../FWUCampaign/FWUCampaignReportModal/utils/formatUtils";
import { metricsConfig } from "./consts/metricsConfig";
import {
  HorizontalChartWrapper,
  MarketsReportWrapper,
  NotificationCampaignReportWrapper
} from "./styled";

const NotificationCampaignReportModal = ({
  active,
  notificationCampaignDetails,
  notificationCampaignData,
  notificationCampaignDetailsLoading,
  loading,
  setActive,
  onSave,
  error,
  onDeactivate
}) => {
  const [isBottomToggled, setIsBottomToggled] = useState(false);

  const notificationCampaignToEdit = useSelector(
    (store) => store.notificationCampaigns?.notificationCampaignToEdit
  );

  useEffect(() => {
    if (!loading && error) {
      setIsBottomToggled(true);
    }
  }, [loading, error]);

  const settingsAvailable = useMemo(() => {
    return notificationCampaignToEdit?.status === campaignStatus.active;
  }, [notificationCampaignToEdit]);

  const reportData = useMemo(() => {
    const generalDetails = formatUtils.formatNotificationCampaignDetails(
      notificationCampaignDetails
    );

    const marketsDetails = formatUtils.formatNotificationCampaignMarketDetails(
      notificationCampaignDetails
    );

    return {
      generalDetails,
      marketsDetails
    };
  }, [notificationCampaignDetails]);

  const onClose = () => {
    dispatch(setNotificationCampaignToEdit(null));
    dispatch(setNotificationCampaignDetails(null));
    setActive(false);
  };

  const onRefreshChartData = () => {
    dispatch(setNotificationCampaignDetailsRefresh(true));
  };

  const onApplyChanges = () => {
    onSave();
  };

  const onDeactivateCampaign = () => {
    onDeactivate();
  };

  return (
    <Modal
      id="new-notification-modal"
      active={active}
      setActive={onClose}
      Container={CustomModalContainer}>
      <NotificationCampaignReportWrapper isBottomToggled={isBottomToggled}>
        <SplittedModalContentTemplate
          title={notificationCampaignData?.campaignName}
          LeftSectionContent={
            <CampaignInfo
              notificationCampaignData={notificationCampaignData}
              loading={loading}
              onDeactivate={onDeactivateCampaign}
              settingsAvailable={settingsAvailable}
            />
          }
          BottomSectionContent={
            settingsAvailable ? (
              <Settings
                isToggled={isBottomToggled}
                disabled={loading}
                reportData={notificationCampaignDetails?.report || {}}
                setIsToggled={() => setIsBottomToggled((prev) => !prev)}
                onApplyChanges={onApplyChanges}
              />
            ) : null
          }>
          {loading ? (
            <Loader />
          ) : (
            <>
              <HorizontalChartWrapper>
                <ReportChart
                  reportData={reportData.generalDetails}
                  metricsConfig={metricsConfig}
                  metricTotal={metricsConfig.received.id}
                  loading={notificationCampaignDetailsLoading}
                  onRefreshChartData={onRefreshChartData}
                  isColumnLegend
                />
              </HorizontalChartWrapper>

              <MarketsReportWrapper>
                {reportData.marketsDetails.map((details) => (
                  <LineChart
                    key={details.market}
                    data={details.report}
                    metricsConfig={metricsConfig}
                    title={details.market}
                    totalDetails={details.totalDetails}
                  />
                ))}
              </MarketsReportWrapper>
            </>
          )}
        </SplittedModalContentTemplate>
      </NotificationCampaignReportWrapper>
    </Modal>
  );
};

NotificationCampaignReportModal.propTypes = {
  active: PropTypes.bool.isRequired,
  notificationCampaignDetails: PropTypes.shape({
    send_notifications: PropTypes.number.isRequired,
    report: PropTypes.shape({
      markets: PropTypes.arrayOf(
        PropTypes.shape({
          market: PropTypes.string.isRequired,
          received: PropTypes.number.isRequired,
          total: PropTypes.number.isRequired
        })
      ).isRequired
    }).isRequired
  }).isRequired,
  notificationCampaignData: PropTypes.shape({
    campaignName: PropTypes.string.isRequired,
    markets: PropTypes.arrayOf(PropTypes.string).isRequired,
    startTs: PropTypes.string.isRequired,
    endTs: PropTypes.string,
    status: PropTypes.string.isRequired,
    audience: PropTypes.shape({
      deviceTypes: PropTypes.arrayOf(PropTypes.string),
      appTypes: PropTypes.arrayOf(PropTypes.string),
      firmwareVersions: PropTypes.arrayOf(PropTypes.string)
    }).isRequired,
    createdBy: PropTypes.shape({
      first_name: PropTypes.string.isRequired,
      last_name: PropTypes.string.isRequired
    }),
    createdDate: PropTypes.string
  }),
  notificationCampaignDetailsLoading: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  setActive: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDeactivate: PropTypes.func.isRequired
};

export default NotificationCampaignReportModal;
